import { Box } from '@mui/system';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useWatch } from 'react-hook-form';

import { ControlledField, FieldTypes } from 'src/components/controlled-field';
import { moveToTop } from 'src/utils/sort-labels';
import {
    selectUserHasBookings,
    selectUserHasSpanishResidenceInfo,
} from 'src/store/slices/user-slice';

import { countries, defaultOption, residency, userDataKeys } from './constants';
import { styles } from './styles';
import { SpanishTaxDetailsFormProps, UserDataFormValues } from './types';
import { useUtils } from './Utils';

export const SpanishTaxDetails: FC<SpanishTaxDetailsFormProps<UserDataFormValues>> = ({
    control,
}) => {
    const { t } = useTranslation();
    const { hasNationality, spanishResidentRequired, nifRequired, nieRequired, passportRequired } =
        useUtils();

    const residencyOptions = [defaultOption, ...residency];

    const nifNumber = useWatch({ control, name: 'nifNumber' });
    const nieNumber = useWatch({ control, name: 'nieNumber' });
    const passportNumber = useWatch({ control, name: 'passportNumber' });
    const passportIssuingCountry = useWatch({ control, name: 'passportIssuingCountry' });

    const sortedIssuingCountries = [
        defaultOption,
        ...moveToTop(t, countries, ['countries_united_kingdom']),
    ];

    const userHasBookings = useSelector(selectUserHasBookings);
    const userSpanishTaxInfo = useSelector(selectUserHasSpanishResidenceInfo);

    const isOccupantsPage = window.location.href.includes('occupants');

    const userHasSpanishResidenceInfo =
        userSpanishTaxInfo ||
        (isOccupantsPage &&
            (!!nifNumber || !!nieNumber || (!!passportNumber && !!passportIssuingCountry)));

    return hasNationality ? (
        <Box css={styles.spanishTaxDetailsContainer}>
            {!nifRequired ? (
                <Box css={styles.inputGroupWrapper}>
                    <ControlledField
                        fieldType={FieldTypes.Select}
                        name={userDataKeys.isSpanishResident}
                        control={control}
                        label={t('your_details_spanish_resident')}
                        placeholder={t('your_details_spanish_resident')}
                        fullWidth
                        options={residencyOptions}
                        required={spanishResidentRequired}
                    />
                </Box>
            ) : null}
            {nifRequired ? (
                <Box css={styles.inputGroupWrapper}>
                    <ControlledField
                        name={userDataKeys.nifNumber}
                        control={control}
                        label={t('your_details_nif')}
                        placeholder={t('your_details_nif')}
                        fullWidth
                        required
                        disabled={userHasBookings && userHasSpanishResidenceInfo}
                    />
                </Box>
            ) : null}
            {nieRequired ? (
                <Box css={styles.inputGroupWrapper}>
                    <ControlledField
                        name={userDataKeys.nieNumber}
                        control={control}
                        label={t('your_details_nie')}
                        placeholder={t('your_details_nie')}
                        fullWidth
                        required={nieRequired}
                        disabled={userHasBookings && userHasSpanishResidenceInfo}
                    />
                </Box>
            ) : null}

            {passportRequired ? (
                <Fragment>
                    <Box css={styles.inputGroupWrapper}>
                        <ControlledField
                            name={userDataKeys.passportNumber}
                            control={control}
                            label={t('your_details_passport')}
                            placeholder={t('your_details_passport')}
                            fullWidth
                            required={passportRequired}
                            disabled={userHasBookings && userHasSpanishResidenceInfo}
                        />
                    </Box>
                    <Box css={styles.inputGroupWrapper}>
                        <ControlledField
                            fieldType={FieldTypes.Select}
                            name={userDataKeys.passportIssuingCountry}
                            control={control}
                            label={t('your_details_passport_country')}
                            fullWidth
                            required
                            options={sortedIssuingCountries}
                            disabled={userHasBookings && userHasSpanishResidenceInfo}
                        />
                    </Box>
                </Fragment>
            ) : null}
        </Box>
    ) : null;
};
